import { onScopeDispose, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'

/** From here the copilot docks: `xl` of @volverjs/style, as in the widget's own styles. */
export const COPILOT_DOCKED_QUERY = '(min-width: 1280px)'

/**
 * The room the docked panel takes: its width, `clamp(320px, 24vw, 406px)` in
 * the widget's styles, plus the frame's margin (`--spacing-12`) on either
 * side of it. In `rem` like the frame, so the two agree on a site with
 * another root font size.
 */
export const COPILOT_PANEL_ROOM = 'calc(clamp(320px, 24vw, 406px) + 1.5rem)'

const STYLE_ID = 'chatbot-copilot-page-css'

// The room the docked panel takes, for the rules below it
const PREAMBLE = `:root { --chatbot-copilot-width: ${COPILOT_PANEL_ROOM}; }\n`

/**
 * The agent's page CSS (spec `widget-copilot.md`, D7): a stylesheet in the
 * host page's `<head>` while the copilot panel is open and docked, and
 * nothing otherwise. It is how a site moves its own elements aside without a
 * change to its code, with `var(--chatbot-copilot-width)`. The text goes in as text,
 * never as markup, and the widget removes only its own sheet.
 */
export function useCopilotPageCss(options: {
    css: () => string | undefined
    active: () => boolean
}): void {
    const docked = useMediaQuery(COPILOT_DOCKED_QUERY)
    let style: HTMLStyleElement | undefined

    const remove = () => {
        style?.remove()
        style = undefined
    }

    watch(
        () =>
            [options.active() && docked.value, options.css()?.trim()] as const,
        ([active, css]) => {
            if (!active || !css) {
                remove()
                return
            }
            if (!style) {
                style = document.createElement('style')
                style.id = STYLE_ID
                document.head.append(style)
            }
            style.textContent = PREAMBLE + css
        },
        { immediate: true },
    )
    onScopeDispose(remove)
}
